// src/App.js

import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { auth, onAuthStateChanged, db, provider, signInWithPopup, functions, httpsCallable } from "./firebase";
import { GoogleAuthProvider } from "firebase/auth";
import { doc, getDoc, getDocs, setDoc, collection } from "firebase/firestore";
import { LinearProgress, Container, Button, Grid, Typography, Box, TextField, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Paper } from "@mui/material";
import CalendarView from "./components/CalendarView";
import JournalEntry from "./components/JournalEntry";
import JournalAssistant from "./components/JournalAssistant";
import LoadingAnimation from "./components/LoadingAnimation";
import PhotoPopupWithGeo from "./components/PhotoPopupWithGeo";
import StoryBookPopup from "./components/StoryBookPopup";
import LogComponent from "./components/LogComponent";
import { LogProvider, useLogs } from "./context/LogContext";
import { AlbumProvider, useAlbum } from "./context/AlbumContext";
import AlbumSwitcher from "./components/AlbumSwitcher";
import moment from 'moment-timezone';
import './App.css';
import { fetchEntryByDate, fetchEntriesByDates, fetchMultipleEntriesByIds, getDriveDisplayUrl } from "./entriesApi";
import { saveEntryMetadata, syncBlocks, savePhotosToSubcollection } from "./albumApi";
import { parseContentIntoBlocks, diffBlocks } from "./utils/blockUtils";
import { entryIdForDate, startOfLocalDay } from "./utils/dateUtils";
import { distanceBetweenCoordinates } from './utils/distanceUtils';
import { findOrCreateFolder, uploadFileToDrive } from "./utils/googleDriveApi";
import { extractGeoDataFromMediaItem } from "./utils/geoDataUtils";
import { getCustomPlacesCached, customPlacesNear } from "./utils/customPlacesApi";
import MapIcon from './assets/map_icon.png';
import { layouts as appSpecificLayouts } from './components/PhotoBook/layouts'; // Renamed to avoid conflict
import { formatEntriesToText } from "./features/storyText/storyTextUtils";
import {
  processEntryForStoryBookInternal,
  generateStoryBookLayoutPlanInternal,
  renderStoryBookWithLayout,
} from "./features/storyBook/storyBookHandler";

import { generateCoverPageAIDetails } from "./features/storyBook/aiHelpers";
import { v4 as uuidv4 } from 'uuid'; // Add uuid import for batch IDs
import LayoutSetEditPopup from "./components/LayoutSetEditPopup"; // Import edit popup
import LayoutEditor from "./components/LayoutEditor"; // Import LayoutEditor
import { initializeGooglePhotosApi, updateGooglePhotosApiToken } from "./utils/googlePhotosApi";
import MenuBookIcon from '@mui/icons-material/MenuBook';
import IconButton from '@mui/material/IconButton';

// Create a Context for the progress state
export const ProgressContext = createContext();

// Fallback preview dimensions if dynamic measurement is not possible
const FALLBACK_PREVIEW_SPREAD_WIDTH_PX = 1200;
const FALLBACK_PREVIEW_SPREAD_HEIGHT_PX = FALLBACK_PREVIEW_SPREAD_WIDTH_PX / 1.414; // A4 landscape spread aspect ratio

// Helper function to format date for display (e.g., in albumDateRangeStr)
const formatDateForDisplay = (date) => {
  if (!date) return '';
  return moment(date).format('MMM D, YYYY');
};

const App = () => {
  // Access log context
  const { logs, logInfo, logError, logSuccess, logAI, logLayout, logPhotos } = useLogs();
  // Access album context
  const { currentAlbumId } = useAlbum();
  const [user, setUser] = useState(null);
  const debugMode = new URLSearchParams(window.location.search).get('debug') === '1';
  const [otherUserId, setOtherUserId] = useState(null);
  const [chatGptToken, setChatGptToken] = useState("");
  const [token, setToken] = useState(localStorage.getItem('googleAccessToken') || null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // New picker-related state
  const [pickerSessionId, setPickerSessionId] = useState(null);
  const [isPollingPicker, setIsPollingPicker] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [uploadProgressLabel, setUploadProgressLabel] = useState('');

  const [currentEntry, setCurrentEntry] = useState({
    title: "",
    content: "",
    aiRewrite: "",
    keywords: [],
    mood: 3,
    image: "",
    photos: []
  });
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ visible: false, message: "", success: true });
  const [geoPopupPhotos, setGeoPopupPhotos] = useState(null);
  const [showTokenInput, setShowTokenInput] = useState(true);
  const [showStoryBookPopup, setShowStoryBookPopup] = useState(false);
  const [storyBookDates, setStoryBookDates] = useState([]);
  const [renderedBook, setRenderedBook] = useState(null);
  const [layoutProgress, setLayoutProgress] = useState(0);
  const [showLayoutProgress, setShowLayoutProgress] = useState(false);
  const [layoutProgressLabel, setLayoutProgressLabel] = useState('');
  const idCounter = useRef(0); // Counter for assigning unique IDs to photos
  const [pdfProgress, setPdfProgress] = useState(0);
  const [showPdfProgress, setShowPdfProgress] = useState(false);
  const [pdfProgressLabel, setPdfProgressLabel] = useState('');
  const [userComment, setUserComment] = useState("");
  const [showGeneratedTextModal, setShowGeneratedTextModal] = useState(false);
  const [generatedStoryText, setGeneratedStoryText] = useState('');
  const [storyTextDates, setStoryTextDates] = useState([]);
  const [showStoryTextPopup, setShowStoryTextPopup] = useState(false);
  const [editPopupOpen, setEditPopupOpen] = useState(false);
  const [currentEditBatchId, setCurrentEditBatchId] = useState(null);
  const [layoutsForCurrentEditBatch, setLayoutsForCurrentEditBatch] = useState([]);
  const [currentCombinedLayoutPlan, setCurrentCombinedLayoutPlan] = useState([]);
  const combinedLayoutPlanRef = useRef([]);
  const [showLayoutEditor, setShowLayoutEditor] = useState(false); // State for LayoutEditor visibility

  const photoMap = useRef({});
  const storyBookDisplayAreaRef = useRef(null);

  const handleCreateStoryText = () => {
    setStoryTextDates([]);
    setShowStoryTextPopup(true);
    logInfo('Started story text creation process');
  };

  const handleCloseStoryTextPopup = () => {
    setShowStoryTextPopup(false);
    setStoryTextDates([]);
  };

  const handleGenerateStoryText = async () => {
    logInfo('Starting story text generation');
    setShowStoryTextPopup(false);
    if (!storyTextDates || storyTextDates.length === 0) {
        setSaveStatus({ visible: true, message: "No dates selected.", success: false });
        return;
    }

    setLoading(true);

    try {
      const dateObjects = storyTextDates.map(dateString => new Date(dateString));
      const entries = await fetchEntriesByDates(dateObjects, chatGptToken, null, currentAlbumId);

      if (!entries || entries.length === 0) {
         setSaveStatus({ visible: true, message: "No entries found for the selected dates.", success: false });
      } else {
        const formattedText = formatEntriesToText(entries);
        setGeneratedStoryText(formattedText); 
        setShowGeneratedTextModal(true);      
        logAI('Generated story text', { length: formattedText.length });
      }

    } catch (error) {
      console.error("Error generating story text:", error);
      setSaveStatus({ visible: true, message: "Error generating story text.", success: false });
    } finally {
      setLoading(false);
      setStoryTextDates([]);
    }
  };

  const handleCloseGeneratedTextModal = () => {
    setShowGeneratedTextModal(false);
    setGeneratedStoryText("");
  };

  const handleCopyToClipboard = async () => {
    logInfo('Copying generated text to clipboard');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(generatedStoryText);
        setSaveStatus({ visible: true, message: "Story text copied to clipboard!", success: true });
        logSuccess('Text copied to clipboard');
      } catch (err) {
        console.error("Failed to copy text: ", err);
        setSaveStatus({ visible: true, message: "Clipboard write failed.", success: false });
      }
    } else {
      setSaveStatus({ visible: true, message: "Clipboard API not available.", success: false });
    }
  };

  // Google Photos Picker Functions
  const processAndUploadPickedPhotos = useCallback(async (pickedItems) => {
    logInfo(`Processing ${pickedItems.length} picked photos for Drive upload.`);
    setShowUploadProgress(true);
    setUploadProgressLabel('Preparing photos...');
    setUploadProgress(0);

    const formattedDateForFolder = moment(selectedDate || new Date()).format('YYYY-MM-DD');
    const registerImportedDriveFile = httpsCallable(functions, 'registerImportedDriveFile');
    const getRenderablePhotoData = httpsCallable(functions, 'getRenderablePhotoData');
    const newPhotoObjectsForEntry = [];
    const CONCURRENT_UPLOADS = 4;

    let myJournalFolderId;
    try {
      myJournalFolderId = await findOrCreateFolder("MyJournal", null, token);
    } catch (error) {
      logError("Failed to find or create 'MyJournal' root folder", error);
      setSaveStatus({ visible: true, message: "Error setting up Drive folder. Cannot save photos.", success: false });
      setShowUploadProgress(false);
      return;
    }

    let dateSpecificFolderId;
    try {
      dateSpecificFolderId = await findOrCreateFolder(formattedDateForFolder, myJournalFolderId, token);
    } catch (error) {
      logError(`Failed to find or create date folder '${formattedDateForFolder}'`, error);
      setSaveStatus({ visible: true, message: "Error setting up date-specific Drive folder.", success: false });
      setShowUploadProgress(false);
      return;
    }

    const uploadQueue = async (items) => {
      let completedUploads = 0;
      for (let i = 0; i < items.length; i += CONCURRENT_UPLOADS) {
        const batch = items.slice(i, i + CONCURRENT_UPLOADS);
        const batchResults = await Promise.all(batch.map(async (item, batchIndex) => {
          try {
            logInfo(`Processing media item for download`, { 
              filename: item.mediaFile.filename, 
              baseUrl: item.mediaFile.baseUrl,
              mimeType: item.mediaFile.mimeType 
            });
            
            setUploadProgressLabel(`Downloading ${item.mediaFile.filename}...`);
            const renderResult = await getRenderablePhotoData({
              googlePhotoBaseUrl: item.mediaFile.baseUrl,
              source: 'picker',
              mimeType: item.mediaFile.mimeType,
              filename: item.mediaFile.filename,
              accessToken: token,
            });

            if (!renderResult.data?.image) {
              throw new Error(`Failed to download photo ${item.mediaFile.filename}: missing image data`);
            }
            const photoResponse = await fetch(renderResult.data.image);
            const photoBlob = await photoResponse.blob();

            setUploadProgressLabel(`Uploading ${item.mediaFile.filename} to Drive...`);
            const driveFile = await uploadFileToDrive(
              photoBlob,
              item.mediaFile.filename,
              item.mediaFile.mimeType || photoBlob.type,
              dateSpecificFolderId,
              token
            );

            const registerResult = await registerImportedDriveFile({
              driveFileId: driveFile.id,
              filename: driveFile.name,
              mimeType: driveFile.mimeType,
            });

            const { latitude, longitude } = await extractGeoDataFromMediaItem(item, otherUserId);

            const photoObject = {
              id: item.id,
              driveFileId: driveFile.id,
              filename: driveFile.name,
              mimeType: driveFile.mimeType,
              baseUrl: registerResult.data.baseUrl || getDriveDisplayUrl(driveFile.id),
              date_taken: item.createTime || (item.mediaFile.mediaFileMetadata && item.mediaFile.mediaFileMetadata.creationTime),
              latitude,
              longitude,
              place: null,
              caption: null,
              story_caption: null,
              story_caption_score: undefined,
              mediaMetadata: item.mediaFile.mediaFileMetadata
            };
            logSuccess(`Successfully processed ${item.mediaFile.filename}`);
            return { success: true, photoObject, filename: item.mediaFile.filename, batchIndex };

          } catch (uploadError) {
            logError(`Failed to process photo ${item.mediaFile.filename}`, { error: uploadError.message });
            return { success: false, filename: item.mediaFile.filename, batchIndex };
          }
        }));

        // Process batch results and update progress
        for (const result of batchResults) {
          if (result.success) {
            newPhotoObjectsForEntry.push(result.photoObject);
          }
          completedUploads++;
          const progressPercentage = Math.round((completedUploads / items.length) * 100);
          setUploadProgress(progressPercentage);
          setUploadProgressLabel(`Processed ${completedUploads}/${items.length}: ${result.filename}`);
        }
      }
    };

    await uploadQueue(pickedItems);

    if (newPhotoObjectsForEntry.length > 0) {
      setCurrentEntry(prevEntry => ({
        ...prevEntry,
        photos: [...prevEntry.photos, ...newPhotoObjectsForEntry],
      }));
      logSuccess(`Added ${newPhotoObjectsForEntry.length} new photos to the current journal entry.`);
    }
    setUploadProgressLabel('All photos processed!');
    setTimeout(() => setShowUploadProgress(false), 2000);
  }, [selectedDate, logInfo, logError, logSuccess, otherUserId, token]);

  const handleOpenGooglePhotosPicker = async () => {
    logInfo('Opening Google Photos Picker');
    if (!token) {
      logError('Google Photos Picker: No access token available. Attempting automatic authentication...');
      const newToken = await handlePopupLogin();
      if (!newToken) {
        return; // User cancelled or login failed
      }
    }

    setLoading(true);
    try {
      logInfo('Creating Google Photos Picker session with token', { tokenExists: !!token });
      const response = await fetch("https://photospicker.googleapis.com/v1/sessions", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json();
        logError('Failed to create Google Photos Picker session', errorData);
        
        if (response.status === 401) {
          // Authentication failed - automatically try popup re-auth
          logInfo('Authentication failed. Attempting automatic re-authentication...');
          const newToken = await retryLogin();
          if (newToken) {
            // Retry the picker session creation with new token
            return await handleOpenGooglePhotosPicker();
          } else {
            setSaveStatus({ 
              visible: true, 
              message: "Authentication failed. Please try again.", 
              success: false 
            });
            return;
          }
        }
        
        throw new Error(`Failed to create picker session: ${errorData.error?.message || response.statusText}`);
      }

      const session = await response.json();
      logInfo('Full session response received', { session: session, sessionId: session.id, pickerUri: session.pickerUri });
      logSuccess('Google Photos Picker session created', { sessionId: session.id });
      
      const pickerWindow = window.open(session.pickerUri, "_blank", "width=800,height=600,resizable=yes,scrollbars=yes");
      if (!pickerWindow) {
          logError('Failed to open picker window. Pop-up blocker?');
          setSaveStatus({ visible: true, message: "Failed to open photo picker. Please disable pop-up blockers.", success: false });
          setLoading(false);
          return;
      }
      
      // Set session ID first, then start polling after a delay
      logInfo('About to set picker session ID', { sessionIdValue: session.id });
      setPickerSessionId(session.id);
      logInfo('Picker session ID set in state');
      
      logInfo('Picker window opened, setting up polling in 2 seconds', { sessionId: session.id });
      setTimeout(() => {
        logInfo('Starting picker polling');
        setIsPollingPicker(true);
      }, 2000);

    } catch (error) {
      logError("Error opening Google Photos Picker", { error: error.message });
      setSaveStatus({ visible: true, message: `Error opening photo picker: ${error.message}`, success: false });
    } finally {
      setLoading(false);
    }
  };

  const fetchPickedMediaItems = useCallback(async (sessionId) => {
    logInfo('Fetching picked media items', { sessionId });
    setLoading(true);
    setShowUploadProgress(true);
    setUploadProgressLabel('Fetching selected photos...');
    setUploadProgress(0);

    try {
      const response = await fetch(`https://photospicker.googleapis.com/v1/mediaItems?sessionId=${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        logError('Failed to fetch picked media items', errorData);
        throw new Error(`Failed to fetch picked media items: ${errorData.error?.message || response.statusText}`);
      }

      const result = await response.json();
      const pickedItems = result.mediaItems || [];
      logSuccess(`Fetched ${pickedItems.length} media items from Picker.`);

      if (pickedItems.length > 0) {
        await processAndUploadPickedPhotos(pickedItems);
      } else {
        logInfo('No media items were selected.');
        setShowUploadProgress(false);
      }

    } catch (error) {
      logError('Error fetching picked media items', { error: error.message });
      setSaveStatus({ visible: true, message: `Error retrieving selected photos: ${error.message}`, success: false });
      setShowUploadProgress(false);
    } finally {
      setLoading(false);
    }
  }, [token, logInfo, logError, logSuccess, processAndUploadPickedPhotos]);

  const handleAlbumDataLoaded = useCallback(async (albumData) => {
    logInfo('Loading album from saved data');
    if (!albumData || !albumData.layouts || albumData.layouts.length === 0) {
      setSaveStatus({ visible: true, message: "Invalid album data.", success: false });
      return;
    }
    
    setLoading(true);
    setShowLayoutProgress(true);
    setLayoutProgressLabel('Loading album data...');
    setLayoutProgress(0);
    
    try {
      // Extract unique journal entry IDs from album data
      const uniqueEntryIds = new Set();
      albumData.layouts.forEach(layout => {
        layout.photos.forEach(photo => {
          if (photo.journalEntryId) {
            uniqueEntryIds.add(photo.journalEntryId);
          }
        });
      });
      
      // Fetch all required entries
      const entryIds = Array.from(uniqueEntryIds);
      const fetchedEntries = await fetchMultipleEntriesByIds(entryIds, (progress) => {
        setLayoutProgress(progress / 2); // First half of progress is fetching entries
      }, currentAlbumId);
      
      // Create a map of entry ID to entry data for easy lookup
      const entriesMap = fetchedEntries.reduce((map, entry) => {
        map[entry.id] = entry;
        return map;
      }, {});
      
      // Reset the photoMap and idCounter for fresh processing
      photoMap.current = {};
      idCounter.current = 0;
      
      // Rehydrate the layout plan with fresh photo data
      let rehydratedLayoutPlan = [];
      
      for (let i = 0; i < albumData.layouts.length; i++) {
        const fileLayout = albumData.layouts[i];
        const layoutProgress = 50 + (i / albumData.layouts.length) * 50;
        setLayoutProgress(layoutProgress);
        
        // Rehydrate photos
        const newPhotoObjects = [];
        
        for (const filePhoto of fileLayout.photos) {
          // Find the entry this photo belongs to
          const entry = entriesMap[filePhoto.journalEntryId];
          
          if (entry && entry.photos) {
            // Find the source photo in the entry
            const sourcePhoto = entry.photos.find(p => 
              (filePhoto.isDriveFile && p.driveFileId === filePhoto.mediaId) || 
              (!filePhoto.isDriveFile && p.id === filePhoto.mediaId)
            );
            
            if (sourcePhoto) {
              // Create a new photo object with a fresh ID
              const newPhotoId = idCounter.current++;
              
              // Start with the source photo data
              const rehydratedPhoto = { ...sourcePhoto };
              
              // Add the new ID and other required fields
              rehydratedPhoto.id = newPhotoId;
              rehydratedPhoto.ai_reference_id = newPhotoId;
              rehydratedPhoto.mediaId = filePhoto.mediaId;
              rehydratedPhoto.isDriveFile = filePhoto.isDriveFile;
              rehydratedPhoto.journalEntryId = filePhoto.journalEntryId;
              
              // Ensure a consistent 'url' property is set for PhotoBookLayout consumption
              // This mirrors what processEntryForStoryBookInternal does
              rehydratedPhoto.url = sourcePhoto.baseUrl || sourcePhoto.url;
              if (!rehydratedPhoto.url) {
                logError("Rehydrated photo is missing crucial URL/BaseURL", { 
                  photoId: newPhotoId, 
                  sourceMediaId: filePhoto.mediaId,
                  sourceEntryId: filePhoto.journalEntryId
                });
              }
              
              // Apply saved crop info
              rehydratedPhoto.cropInfo = filePhoto.cropInfo || { zoom: 1, crop: { x: 0, y: 0 }, croppedAreaPercentages: null };
              
              // Store the URL in photoMap
              photoMap.current[newPhotoId] = rehydratedPhoto.url;
              
              newPhotoObjects.push(rehydratedPhoto);
            } else {
              logError('Source photo not found in entry', { 
                mediaId: filePhoto.mediaId, 
                journalEntryId: filePhoto.journalEntryId,
                isDriveFile: filePhoto.isDriveFile 
              });
            }
          } else {
            logError('Entry not found for photo', { journalEntryId: filePhoto.journalEntryId });
          }
        }
        
        // Create the rehydrated layout object with common properties
        const layoutEntryForPlan = {
          layout_id: fileLayout.layout_id,
          photos: newPhotoObjects,
          photo_place_names: fileLayout.photo_place_names || [],
          explanation: fileLayout.explanation, // Make sure to load explanation too if saved
          // Rehydrate leftPageData and rightPageData if they exist in the file (for cover page)
          leftPageData: fileLayout.leftPageData || (fileLayout.layout_id === 'cover' ? { textElements: [] } : undefined),
          rightPageData: fileLayout.rightPageData || (fileLayout.layout_id === 'cover' ? { textElements: [] } : undefined),
          ai_batch_id: fileLayout.ai_batch_id,
          source_entry_ids: fileLayout.source_entry_ids,
          placeholderModifications: fileLayout.placeholderModifications || [],
        };

        // Only support version 2.1
        if (albumData.albumVersion !== "2.1") {
          logError('Attempted to load unsupported album version.', { version: albumData.albumVersion });
          setSaveStatus({ visible: true, message: `Unsupported album version: ${albumData.albumVersion}. Please use version 2.1.`, success: false });
          setLoading(false);
          setShowLayoutProgress(false);
          return;
        }
        
        // Load per-page fields from version 2.1
        layoutEntryForPlan.left_page_text = fileLayout.left_page_text || '';
        layoutEntryForPlan.right_page_text = fileLayout.right_page_text || '';
        layoutEntryForPlan.left_page_display_date = fileLayout.left_page_display_date || '';
        layoutEntryForPlan.left_page_display_city = fileLayout.left_page_display_city || '';
        layoutEntryForPlan.right_page_display_date = fileLayout.right_page_display_date || '';
        layoutEntryForPlan.right_page_display_city = fileLayout.right_page_display_city || '';
        layoutEntryForPlan.adjusted_left_page_font_size = fileLayout.adjusted_left_page_font_size;
        layoutEntryForPlan.adjusted_right_page_font_size = fileLayout.adjusted_right_page_font_size;
        
        rehydratedLayoutPlan.push(layoutEntryForPlan);
      }
      
      // Don't call renderStoryBookWithLayout directly here - let the central useEffect handle it
      logInfo('Rendering loaded album', { layoutCount: rehydratedLayoutPlan.length });
      
      setLayoutProgress(100);
      setSaveStatus({ visible: true, message: "Album loaded successfully!", success: true });
      combinedLayoutPlanRef.current = rehydratedLayoutPlan; // Update ref immediately
      setCurrentCombinedLayoutPlan(rehydratedLayoutPlan); // This will trigger the central useEffect
    } catch (error) {
      console.error('Error loading album:', error);
      logError('Failed to load album', { error: error.message });
      setSaveStatus({ visible: true, message: `Error loading album: ${error.message}`, success: false });
    } finally {
      setLoading(false);
      setShowLayoutProgress(false);
    }
  }, [logInfo, logError, currentAlbumId]);

  const handleGenerateStoryBook = async () => {
    logInfo('Starting story book generation process');
    if (debugMode) logInfo('Dates selected for story book', { dates: storyBookDates });
    logInfo('Calling fetchEntriesByDates API', { dates: storyBookDates });
    
    setShowStoryBookPopup(false);
    setLoading(true); 

    setShowLayoutProgress(true);
    setLayoutProgressLabel('Generating layout...');
    setLayoutProgress(0);

    // Determine preview dimensions for text fitting
    let spreadPreviewWidthPx = FALLBACK_PREVIEW_SPREAD_WIDTH_PX;
    let spreadPreviewHeightPx = FALLBACK_PREVIEW_SPREAD_HEIGHT_PX;

    if (storyBookDisplayAreaRef.current && storyBookDisplayAreaRef.current.clientWidth > 0) {
      const containerClientWidth = storyBookDisplayAreaRef.current.clientWidth;
      // The PhotoBookRenderer's main content div takes 90% of this width.
      // This 90% width div is the one that contains the spreads.
      // Each spread (PhotoBookLayout) takes 100% width of that 90% div.
      spreadPreviewWidthPx = containerClientWidth * 0.90;
      spreadPreviewHeightPx = spreadPreviewWidthPx / 1.414; // Aspect ratio of the spread
      logLayout('Using preview dimensions from storyBookDisplayAreaRef', { width: spreadPreviewWidthPx, height: spreadPreviewHeightPx });
    } else {
      logLayout(`storyBookDisplayAreaRef not available or has no width. Using default preview dimensions: ${spreadPreviewWidthPx}x${spreadPreviewHeightPx}`, {
        refExists: !!storyBookDisplayAreaRef.current,
        clientWidth: storyBookDisplayAreaRef.current?.clientWidth
      });
    }

    // Final sanity check for dimensions
    if (spreadPreviewWidthPx <= 0 || spreadPreviewHeightPx <= 0) {
      logError(`Preview dimensions ${spreadPreviewWidthPx}x${spreadPreviewHeightPx} are invalid. Critical fallback to defaults.`, {});
      spreadPreviewWidthPx = FALLBACK_PREVIEW_SPREAD_WIDTH_PX;
      spreadPreviewHeightPx = FALLBACK_PREVIEW_SPREAD_HEIGHT_PX;
    }

    try {
      const dateObjects = storyBookDates.map(dateString => new Date(dateString));
      const entries = await fetchEntriesByDates(dateObjects, chatGptToken, null, currentAlbumId);

      const idCounter = { current: 0 };
      photoMap.current = {};
      let priorGeneratedTexts = [];
      let combinedLayoutPlan = []; 
      let currentPendingEntries = [];

      entries.sort((a, b) => new Date(a.createdAt.seconds * 1000) - new Date(b.createdAt.seconds * 1000));
      
      let allProcessedPhotosForCover = [];
      let fullAlbumContentForCover = "";
      let allKeywordsAndPlacesForCover = new Set();

      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const processedEntry = processEntryForStoryBookInternal(entry, idCounter, photoMap);
        currentPendingEntries.push(processedEntry);

        // Accumulate data for cover generation
        allProcessedPhotosForCover.push(...processedEntry.photos);
        fullAlbumContentForCover += `${entry.title || ''}
${entry.content}
`;
        processedEntry.photos.forEach(p => { if (p.place && p.place.name) allKeywordsAndPlacesForCover.add(p.place.name); });
        if(entry.keywords) entry.keywords.forEach(k => allKeywordsAndPlacesForCover.add(k));

        const layoutsJsonWithTotalPhotos = appSpecificLayouts.map(layout => {
          const leftPagePhotos = layout.leftPage.photoPlaceholders ? layout.leftPage.photoPlaceholders.length : 0;
          const rightPagePhotos = layout.rightPage.photoPlaceholders ? layout.rightPage.photoPlaceholders.length : 0;
          const totalPhotos = leftPagePhotos + rightPagePhotos;
      
          return {
            totalPhotos,
            ...layout
          };
        });

        const currentBatchPhotos = currentPendingEntries.flatMap(e => e.photos);
        const interestingPhotosInBatch = currentBatchPhotos.filter(photo => photo.story_caption_score >= 70);
        
        // Filter out the cover layout when calculating the minimum photos required
        const contentLayoutsWithPhotos = layoutsJsonWithTotalPhotos.filter(layout => layout.id !== 'cover');
        const minLayoutPhotos = contentLayoutsWithPhotos.length > 0
          ? Math.min(...contentLayoutsWithPhotos.map(l => l.totalPhotos))
          : 0;
        const currentBatchIsSufficientlyInteresting = minLayoutPhotos > 0 && interestingPhotosInBatch.length >= minLayoutPhotos;
        
        const isLastEntryInLoop = i === entries.length - 1;

        if (debugMode) {
          logInfo('Processing entry for storybook', {
            index: i + 1,
            date: processedEntry.createdAt,
            photoCount: processedEntry.photos.length,
            interestingPhotosInBatch: interestingPhotosInBatch.length,
            minLayoutPhotos,
            currentBatchIsSufficientlyInteresting
          });
        }

        if (currentBatchIsSufficientlyInteresting || isLastEntryInLoop) {
          if (currentPendingEntries.length === 0) continue;

          const combinedContent = currentPendingEntries
            .map(e => `${e.createdAt}\n${e.content}`)
            .join('\n');
          const entryToGenerateForAPI = {
            ...(currentPendingEntries[0]),
            photos: currentPendingEntries.flatMap(e => e.photos),
            content: combinedContent,
          };

          const currentBatchId = uuidv4(); // Unique ID for this layout batch
          const sourceEntryIdsForBatch = currentPendingEntries.map(e => e.id); // Collect source entry IDs

          const { layoutPlan: batchLayoutPlan, generatedText } =
            await generateStoryBookLayoutPlanInternal(
              entryToGenerateForAPI, 
              priorGeneratedTexts, 
              chatGptToken, 
              debugMode, 
              userComment, 
              photoMap, 
              logAI,
              logLayout,
              spreadPreviewWidthPx,  // Pass calculated spread width
              spreadPreviewHeightPx  // Pass calculated spread height
            );

          // Tag layouts with batch and source IDs
          batchLayoutPlan.forEach((l) => {
            l.ai_batch_id = currentBatchId;
            l.source_entry_ids = sourceEntryIdsForBatch;
          });

          combinedLayoutPlan = combinedLayoutPlan.concat(batchLayoutPlan);
          priorGeneratedTexts.push(generatedText);
          currentPendingEntries = [];
        }

        const currentLayoutProgress = Math.round(((i + 1) / entries.length) * 100);
        setLayoutProgress(currentLayoutProgress);
      }
      setShowLayoutProgress(false);

      // --- Generate Cover Page ---
      if (allProcessedPhotosForCover.length > 0 && entries.length > 0) {
        const bestCoverPhoto = allProcessedPhotosForCover.reduce((best, current) => {
            return ((current.story_caption_score || 0) > (best.story_caption_score || 0)) ? current : best;
        }, allProcessedPhotosForCover[0]);

        const albumStartDate = new Date(entries[0].createdAt.seconds * 1000);
        const albumEndDate = new Date(entries[entries.length - 1].createdAt.seconds * 1000);
        const albumDateRangeStr = `${formatDateForDisplay(albumStartDate)} - ${formatDateForDisplay(albumEndDate)}`;

        const summaryForAI = fullAlbumContentForCover.substring(0, 1500) + (fullAlbumContentForCover.length > 1500 ? "..." : "");
        const keywordsList = Array.from(allKeywordsAndPlacesForCover).slice(0, 15);

        try {
            logAI('Generating cover page details...');
            const coverAIDetails = await generateCoverPageAIDetails(
                {
                    caption: bestCoverPhoto.caption,
                    story_caption: bestCoverPhoto.story_caption,
                    place: bestCoverPhoto.place ? bestCoverPhoto.place.name : null,
                },
                albumDateRangeStr,
                summaryForAI,
                keywordsList,
                chatGptToken,
                logAI
            );
            logAI('Cover page details received', coverAIDetails);

            const coverLayoutDefinition = appSpecificLayouts.find(l => l.id === 'cover');
            if (coverLayoutDefinition) {
                const coverPageLayoutObject = {
                    layout_id: 'cover',
                    photos: [bestCoverPhoto], // Ensure bestCoverPhoto has url and default cropInfo
                    left_page_text: coverAIDetails.title,
                    left_page_display_date: albumDateRangeStr,
                    display_city: '', // Cover doesn't usually have a specific city
                    photo_place_names: bestCoverPhoto.place ? [bestCoverPhoto.place.name] : [],
                    explanation: coverAIDetails.explanation || 'Album Cover and Back',
                    // Populate leftPageData and rightPageData directly for consistency
                    leftPageData: { // Front Cover dynamic data
                        textElements: (coverLayoutDefinition.leftPage.scatteredWordPlaceholders || []).map((placeholder, idx) => ({
                            ...placeholder, // Includes position and style from layout definition
                            content: coverAIDetails.scatteredWordsFront[idx] || "" // AI-generated content
                        }))
                    },
                    rightPageData: { // Back Cover dynamic data
                        textElements: (coverLayoutDefinition.rightPage.scatteredWordPlaceholders || []).map((placeholder, idx) => ({
                            ...placeholder, // Includes position and style from layout definition
                            content: coverAIDetails.scatteredWordsBack[idx] || "" // AI-generated content
                        }))
                    },
                    // The adjustedPageTextFontSize is typically for the main 'text' prop.
                    // The cover's main title's font size is defined in layouts.js.
                };
                combinedLayoutPlan.unshift(coverPageLayoutObject);
            } else { logError('Cover layout definition (id: "cover") not found!'); }
        } catch (error) { logError('Failed to generate cover page details', { error: error.message, stack: error.stack }); }
      }
      // --- End Generate Cover Page ---

      // Create a PhotoBook with the layouts
      const bookRender = renderStoryBookWithLayout(
        combinedLayoutPlan,
        logLayout,
        spreadPreviewWidthPx,
        handleAlbumDataLoaded, // Pass the callback for album loading
        handleRequestEditBatch, // Pass the edit handler
        handleLayoutPlanUpdate, // Pass the new handler
        handlePhotoCropChangeInPlan, // ADD THIS
        currentAlbumId
      );
      setRenderedBook(bookRender);
      combinedLayoutPlanRef.current = combinedLayoutPlan; // Update ref immediately
      setCurrentCombinedLayoutPlan(combinedLayoutPlan);

    } catch (error) {
      console.error("Error generating story book:", error);
      logError('Storybook generation failed', { error: error.message, stack: error.stack });
      setSaveStatus({ visible: true, message: `Error generating story book: ${error.message}`, success: false });
      setShowLayoutProgress(false);
    }
    setLoading(false);
  };

  const progressContextValue = {
    loading,
    setLoading,
    layoutProgress, setLayoutProgress,
    showLayoutProgress, setShowLayoutProgress,
    layoutProgressLabel, setLayoutProgressLabel,
    pdfProgress, setPdfProgress,
    showPdfProgress, setShowPdfProgress,
    pdfProgressLabel, setPdfProgressLabel
  };
  
  const fetchOtherUserId = async () => {
    const user = auth.currentUser;

    if (user) {
      const shareCollectionRef = collection(db, `users/${user.uid}/share`);
      const shareSnapshot = await getDocs(shareCollectionRef);

      if (!shareSnapshot.empty) {
        const firstDoc = shareSnapshot.docs[0];
        return firstDoc.id;  
      }
    }
    return null;
  };

  const groupPhotosByDistance = (photos, distanceThreshold) => {
    const groups = [];
    photos.forEach(photo => {
      let addedToGroup = false;
      for (const group of groups) {
        if (group.some(p => distanceBetweenCoordinates(p, photo) <= distanceThreshold)) {
          group.push(photo);
          addedToGroup = true;
          break;
        }
      }
      if (!addedToGroup) {
        groups.push([photo]);
      }
    });
    return groups;
  };

  const fetchPlaceForGroup = async (photo, pageToken = null, text = '') => {
    const getNearbyPlaces = httpsCallable(functions, 'getNearbyPlaces');
    try {
      const result = await getNearbyPlaces({ lat: photo.latitude, lng: photo.longitude, pageToken, query: text });
      const places = result.data.places || [];

      // Prepend any custom places near this photo so they appear above Google results.
      if (!pageToken) {
        const customPlaces = await getCustomPlacesCached();
        const nearbyCustom = customPlacesNear(customPlaces, photo.latitude, photo.longitude);
        const customPlaceOptions = nearbyCustom
          .filter(cp => !places.some(pl => pl.place_id === cp.place_id))
          .map(cp => ({ place_id: cp.place_id, name: cp.name, types: cp.types }));
        places.unshift(...customPlaceOptions);
      }

      if (result.data.nextPageToken) {
        places.push({
          name: 'Show more results',
          place_id: 'show_more',
          nextPageToken: result.data.nextPageToken,
        });
      }
  
      return { places };
    } catch (error) {
      console.error('Error fetching nearby places:', error);
      return { places: [] };
    }
  };

  const fetchMorePlacesForGroup = async (photo, token) => {
    const group = currentEntry.photos.filter(p => distanceBetweenCoordinates(p, photo) <= 10);
    
    const result = await fetchPlaceForGroup(photo, token);
    
    group.forEach(p => {
      p.places = [
        ...p.places.filter(place => place.place_id !== 'show_more'),
        ...result.places
      ];
    });

    setCurrentEntry({
      ...currentEntry,
      photos: [...currentEntry.photos]
    });
  };

  const handlePhotoSelectionChange = (photo, isSelected) => {
    logPhotos(`Photo ${isSelected ? 'selected' : 'deselected'}`, { photoId: photo.id });
    setCurrentEntry(prevEntry => ({
      ...prevEntry,
      photos: isSelected
        ? [...prevEntry.photos, photo]
        : prevEntry.photos.filter(p => p.id !== photo.id)
    }));
  };

  const saveEntry = async () => {
    logInfo('Saving journal entry', { date: selectedDate });
    if (!user || !currentAlbumId) {
      logError('Cannot save: no user or album selected');
      setSaveStatus({ visible: true, message: 'Please select an album first', success: false });
      return;
    }

    try {
      // Step 1: Save entry metadata
      const metadata = {
        title: currentEntry.title || '',
        mood: currentEntry.mood || 3,
        keywords: currentEntry.keywords || [],
        weather: currentEntry.weather || '',
        image: currentEntry.image || '',
        createdAt: currentEntry.createdAt || startOfLocalDay(selectedDate),
        timezone: currentEntry.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      const targetEntryId = currentEntry.id || entryIdForDate(selectedDate);

      const entryId = await saveEntryMetadata(
        currentAlbumId,
        targetEntryId,
        metadata,
        user
      );

      // Update current entry with the ID if it's new
      if (!currentEntry.id) {
        setCurrentEntry({ ...currentEntry, id: entryId });
      }

      // Step 2: Parse content into blocks and sync
      const newBlocks = parseContentIntoBlocks(currentEntry.content || '');
      const existingBlocks = currentEntry.blocks || [];
      const { toAdd, toUpdate, toDelete } = diffBlocks(existingBlocks, newBlocks);

      logInfo('Block sync', {
        toAdd: toAdd.length,
        toUpdate: toUpdate.length,
        toDelete: toDelete.length,
      });

      await syncBlocks(currentAlbumId, entryId, toAdd, toUpdate, toDelete, user);

      // Step 3: Save photos to subcollection
      if (currentEntry.photos && currentEntry.photos.length > 0) {
        await savePhotosToSubcollection(
          currentAlbumId,
          entryId,
          currentEntry.photos,
          user
        );
        logInfo('Saved photos to subcollection', { count: currentEntry.photos.length });
      }

      logSuccess('Entry saved successfully to album', { 
        albumId: currentAlbumId,
        entryId,
        date: selectedDate 
      });
      setSaveStatus({ visible: true, message: "Save successful!", success: true });

      // Reload the entry to get the updated blocks
      const reloadedEntry = await fetchEntryByDate(selectedDate, currentAlbumId);
      if (reloadedEntry) {
        setCurrentEntry(reloadedEntry);
      }
    } catch (error) {
      console.error("Error saving entry:", error); 
      logError('Failed to save entry', { error: error.message, stack: error.stack });
      setSaveStatus({ visible: true, message: `Save failed! ${error.message}`, success: false });
    }
  };

  const handleDateSelect = useCallback(async (date) => {
    if (!user) return;
    setSelectedDate(date);
    let entry = await fetchEntryByDate(date, currentAlbumId);

    if (entry) {
      setCurrentEntry(entry);
    } else {
      setCurrentEntry({
        title: "",
        content: "",
        aiRewrite: "",
        keywords: [],
        mood: 3,
        image: "",
        photos: []
      });
    }
  }, [user, currentAlbumId]);

  // Polling useEffect for Google Photos Picker
  useEffect(() => {
    logInfo('Polling useEffect triggered', { 
      isPollingPicker, 
      pickerSessionId, 
      tokenExists: !!token,
      pickerSessionIdType: typeof pickerSessionId,
      pickerSessionIdValue: pickerSessionId
    });
    
    if (!isPollingPicker || !pickerSessionId || !token) {
      logInfo('Not starting polling - missing requirements', { 
        isPollingPicker, 
        pickerSessionId, 
        tokenExists: !!token,
        missingPollingFlag: !isPollingPicker,
        missingSessionId: !pickerSessionId,
        missingToken: !token
      });
      return;
    }

    logInfo('Starting polling interval', { sessionId: pickerSessionId });
    
    let pollInterval;
    let isCleanedUp = false;

    const pollSessionStatus = async () => {
      if (isCleanedUp) {
        logInfo('Skipping poll - component cleaned up');
        return;
      }
      
      logInfo('Polling picker session status', { sessionId: pickerSessionId });
      try {
        const response = await fetch(`https://photospicker.googleapis.com/v1/sessions/${pickerSessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 404) {
              logError('Picker session not found or expired. Stopping poll.', errorData);
              // Clean up and stop polling
              if (pollInterval) clearInterval(pollInterval);
              isCleanedUp = true;
              // Use setTimeout to avoid triggering useEffect during render
              setTimeout(() => {
                setIsPollingPicker(false);
                setPickerSessionId(null);
                setSaveStatus({ visible: true, message: "Photo selection session expired or was cancelled.", success: false });
              }, 0);
              return;
          }
          logError('Error polling picker session', errorData);
          return; 
        }

        const sessionStatus = await response.json();
        logInfo('Picker session status', sessionStatus);

        if (sessionStatus.mediaItemsSet) {
          logSuccess('User has completed photo selection in Picker.');
          // Clean up polling first
          if (pollInterval) clearInterval(pollInterval);
          isCleanedUp = true;
          
          // Fetch media items and then clean up state
          try {
            await fetchPickedMediaItems(pickerSessionId);
          } finally {
            // Use setTimeout to avoid triggering useEffect during render
            setTimeout(() => {
              setIsPollingPicker(false);
              setPickerSessionId(null);
            }, 0);
          }
        } else if (sessionStatus.sessionState === 'EXPIRED' || sessionStatus.sessionState === 'CANCELLED') {
          logInfo(`Picker session ${sessionStatus.sessionState}. Stopping poll.`);
          // Clean up and stop polling
          if (pollInterval) clearInterval(pollInterval);
          isCleanedUp = true;
          // Use setTimeout to avoid triggering useEffect during render
          setTimeout(() => {
            setIsPollingPicker(false);
            setPickerSessionId(null);
            setSaveStatus({ visible: true, message: `Photo selection ${sessionStatus.sessionState.toLowerCase()}.`, success: false });
          }, 0);
        }
      } catch (error) {
        logError('Exception during picker session poll', { error: error.message });
        // Clean up and stop polling
        if (pollInterval) clearInterval(pollInterval);
        isCleanedUp = true;
        setTimeout(() => setIsPollingPicker(false), 0);
      }
    };

    // Start polling immediately and then set interval
    pollSessionStatus(); 
    pollInterval = setInterval(pollSessionStatus, 5000);

    return () => {
      isCleanedUp = true;
      if (pollInterval) {
        logInfo('Cleaning up polling interval');
        clearInterval(pollInterval);
      }
    };
  }, [isPollingPicker, pickerSessionId, token, logInfo, logError, logSuccess, fetchPickedMediaItems]);

  // Popup login function that doesn't require full logout
  const handlePopupLogin = useCallback(async () => {
    try {
      logInfo('Triggering automatic popup re-authentication');
      setSaveStatus({ 
        visible: true, 
        message: "Re-authenticating to grant required permissions...", 
        success: true 
      });
      
      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential.accessToken;
      
      if (accessToken) {
        localStorage.setItem('googleAccessToken', accessToken);
        setToken(accessToken);
        // Also update the Google Photos API token
        updateGooglePhotosApiToken(accessToken);
        logSuccess('Popup re-authentication successful with new scopes');
        setSaveStatus({ 
          visible: true, 
          message: "Re-authentication successful! You can now continue.", 
          success: true 
        });
        return accessToken;
      } else {
        logError('No access token received from popup login');
        setSaveStatus({ 
          visible: true, 
          message: "Re-authentication failed. Please try again.", 
          success: false 
        });
        return null;
      }
    } catch (error) {
      console.error('Popup login failed:', error);
      logError('Popup re-authentication failed', { error: error.message });
      
      if (error.code === 'auth/popup-closed-by-user') {
        setSaveStatus({ 
          visible: true, 
          message: "Authentication was cancelled. Please try again when ready.", 
          success: false 
        });
      } else {
        setSaveStatus({ 
          visible: true, 
          message: `Re-authentication failed: ${error.message}`, 
          success: false 
        });
      }
      return null;
    }
  }, [logInfo, logError, logSuccess]);

  // Retry login function for Google Photos API
  const retryLogin = useCallback(async () => {
    try {
      logInfo('Attempting to refresh Google access token...');
      //const refreshGoogleAccessToken = httpsCallable(functions, 'refreshGoogleAccessToken');
      //const result = await refreshGoogleAccessToken();
      //const newToken = result.data.accessToken;
      // Token refresh is not implemented, use popup login instead
      return await handlePopupLogin();
    } catch (error) {
      logError('Failed to refresh Google access token', { error: error.message });
      
      // If refresh fails, automatically trigger popup login instead of showing error message
      if (error.code === 'permission-denied' || error.code === 'functions/permission-denied' || 
          error.code === 'not-found' || error.code === 'functions/not-found') {
        logInfo('Refresh token invalid or missing scopes, automatically triggering popup re-authentication');
        
        // Clear the invalid/insufficient token
        localStorage.removeItem('googleAccessToken');
        setToken(null);
        
        // Automatically trigger popup login
        return await handlePopupLogin();
      }
      return null;
    }
  }, [logInfo, logError, handlePopupLogin]);

  const handleLogin = async () => {
    const newToken = await handlePopupLogin();
    if (newToken) {
      // Ensure Google Photos API is initialized with the new token
      initializeGooglePhotosApi(newToken, retryLogin);
      logInfo('Google Photos API re-initialized after popup login');
    }
    return newToken;
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('googleAccessToken');
      setToken(null);
      setUser(null);
      logSuccess('Logout successful');
      setSaveStatus({ visible: true, message: "Logged out successfully.", success: true });
    } catch (error) {
      console.error('Logout failed:', error);
      logError('Logout failed', { error: error.message });
    }
  };

  useEffect(() => {
    const fetchInitialData = async (accessToken) => {
      logInfo('Initializing app data');
      await handleDateSelect(new Date());
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        const otherUserId = await fetchOtherUserId();  
        if (otherUserId) {
          setOtherUserId(otherUserId);  
        }
        const tokenDoc = await getDoc(doc(db, "tokens", user.uid));
        if (tokenDoc.exists()) {
          setChatGptToken(tokenDoc.data().token);
          setShowTokenInput(false); 
        }
        
        // Initialize Google Photos API with current token and retry function
        if (token) {
          initializeGooglePhotosApi(token, retryLogin);
          logInfo('Google Photos API initialized with token and retry function');
        }
        
        await fetchInitialData(token);
      }
    });
    return () => unsubscribe();
  }, [handleDateSelect, token, logInfo, retryLogin]);

  // Initialize Google Photos API when token changes
  useEffect(() => {
    if (token && retryLogin) {
      initializeGooglePhotosApi(token, retryLogin);
      logInfo('Google Photos API reinitialized with updated token');
    }
  }, [token, retryLogin, logInfo]);

  const saveToken = async () => {
    if (chatGptToken && auth.currentUser) {
      try {
        await setDoc(doc(db, "tokens", auth.currentUser.uid), {
          token: chatGptToken,
        });
        logSuccess('Token saved successfully');
        alert("Token saved successfully!");
        setShowTokenInput(false); 
      } catch (error) {
        console.error("Error saving token: ", error);
        logError('Failed to save token', { error: error.message });
        alert("Failed to save token.");
      }
    }
  };

  const handleGeoPopupOpen = async () => {
    setLoading(true);
    const photosWithGeo = currentEntry.photos.filter(
      photo => photo.latitude && photo.longitude && photo.latitude !== 0 && photo.longitude !== 0
    );

    const groups = groupPhotosByDistance(photosWithGeo, 10); 

    const placePromises = groups.map(async group => {
      const response = await fetchPlaceForGroup(group[0]); 
      return { group, places: response.places }; 
    });

    const placeResults = await Promise.all(placePromises);

    const updatedPhotos = currentEntry.photos.map(photo => {
      const result = placeResults.find(result =>
        result.group.some(p => p.id === photo.id)
      );

      if (result) {
        if (photo.place && !result.places.some(pl => pl.place_id === photo.place.place_id)) {
          result.places = [photo.place, ...result.places];
        }
        return { ...photo, places: result.places };
      }

      return photo;
    });

    setCurrentEntry({
      ...currentEntry,
      photos: updatedPhotos,
    });

    setLoading(false);
    openGeoPhotoPopup(updatedPhotos);
  };

  const openGeoPhotoPopup = (photos) => {
    setGeoPopupPhotos(photos);
  };

  const closeGeoPhotoPopup = () => {
    setGeoPopupPhotos(null);
  };

  const handleCreateStoryBook = () => {
    logInfo('Starting story book creation process');
    setStoryBookDates([]);
    setShowStoryBookPopup(true);
  };

  const handleCloseStoryBookPopup = () => {
    setShowStoryBookPopup(false);
    setStoryBookDates([]);
    logInfo('Closed story book selection dialog');
  };

  const handleRequestEditBatch = useCallback((batchId) => {
    // Access the plan from the ref for the most up-to-date version
    const planToUse = combinedLayoutPlanRef.current;
    
    if (!planToUse.length) {
      logError("Cannot edit batch: layout plan not ready.");
      setSaveStatus({ visible: true, message: "Error: Layout plan not ready for editing.", success: false });
      return;
    }
    const layouts = planToUse.filter(l => l.ai_batch_id === batchId);
    if (!layouts.length) {
      logError(`No layouts found for batchId: ${batchId}`);
      setSaveStatus({ visible: true, message: "Error: Could not find layouts for this set.", success: false });
      return;
    }
    setLayoutsForCurrentEditBatch(layouts);
    setCurrentEditBatchId(batchId);
    setEditPopupOpen(true);
    logInfo('Requesting edit for batch', { batchId, numLayouts: layouts.length });
  }, [logError, logInfo]);

  const handleRegenerateLayoutSet = async (batchIdToRegenerate, chosenPhotos, extraInstructions, sourceEntryIds) => {
    const planBeforeRegen = combinedLayoutPlanRef.current; // Get current plan from ref
    setEditPopupOpen(false);
    setLoading(true);
    setShowLayoutProgress(true);
    setLayoutProgressLabel(`Regenerating layout set (Batch ID: ${batchIdToRegenerate.substring(0,8)})...`);
    setLayoutProgress(0);
    try {
      // Prepare content for AI
      let entryContent = '';
      if (sourceEntryIds?.length) {
        const entries = await fetchMultipleEntriesByIds(sourceEntryIds, (p) => setLayoutProgress(10 + p * 0.2), currentAlbumId);
        entryContent = entries.map(e => `${new Date(e.createdAt.seconds*1000).toLocaleDateString()}\n${e.content||''}`).join('\n\n');
      } else {
        const existingLayoutsInBatch = planBeforeRegen.filter(l => l.ai_batch_id === batchIdToRegenerate);
        entryContent = existingLayoutsInBatch.map(l => `${l.left_page_text||''} ${l.right_page_text||''}`.trim()).join('\n');
      }

      /* ------------------------------------------------------------
       * 1.  Re-run the normal "processEntry…" helper so every photo
       *     gets id / ai_reference_id / journalEntryId / url / crop…
       * ---------------------------------------------------------- */
      const idCounterLocal = { current: 0 };

      // fetch the original entries once; we already need them for entryContent
      const sourceEntries =
            sourceEntryIds?.length
              ? await fetchMultipleEntriesByIds(
                  sourceEntryIds,
                  p => setLayoutProgress( 10 + p * 0.2 ),      // keep your progress bar
                  currentAlbumId
                )
              : [];

      // run the helper on every source entry → collect processed photos
      const processedPhotosFromEntries = [];
      sourceEntries.forEach(entry => {
        const processed = processEntryForStoryBookInternal(
            entry,
            idCounterLocal,
            photoMap                                   // same shared ref
        );
        processedPhotosFromEntries.push(...processed.photos);
      });

      /* Keep only the photos the user actually kept in the edit popup,
       * but preserve their original order */
      const processedPhotoMap = new Map(
            processedPhotosFromEntries.map(p => [
               /* key = DriveFileId or GooglePhotosId */
               p.isDriveFile ? p.mediaId : p.mediaId,
               p
            ]));

      const processedChosenPhotos = chosenPhotos
            .map(raw => processedPhotoMap.get(
                    raw.driveFileId || raw.id
                  ))
            .filter(Boolean);                         // should all be found

      const dummyEntry = {
        id: `regen-${uuidv4()}`,
        createdAt: new Date().toISOString(),
        content: entryContent,
        photos: processedChosenPhotos                // ⇦ now have journalEntryId
      };
      /* ---------- work out the same preview size we used originally ---------- */
      let spreadPreviewWidthPx = FALLBACK_PREVIEW_SPREAD_WIDTH_PX;
      let spreadPreviewHeightPx = FALLBACK_PREVIEW_SPREAD_HEIGHT_PX;
      if (storyBookDisplayAreaRef.current && storyBookDisplayAreaRef.current.clientWidth > 0) {
        spreadPreviewWidthPx = storyBookDisplayAreaRef.current.clientWidth * 0.90;
        spreadPreviewHeightPx = spreadPreviewWidthPx / 1.414; // A4 aspect
      }

      setLayoutProgress(35);
      const { layoutPlan: newLayouts } = await generateStoryBookLayoutPlanInternal(dummyEntry, [], chatGptToken, debugMode, extraInstructions, photoMap, logAI, logLayout, spreadPreviewWidthPx, spreadPreviewHeightPx);
      setLayoutProgress(70);
      // Tag new layouts
      newLayouts.forEach(l => { l.ai_batch_id = batchIdToRegenerate; l.source_entry_ids = sourceEntryIds; });
      // Replace old batch
      const firstIdx = planBeforeRegen.findIndex(l => l.ai_batch_id===batchIdToRegenerate);
      const withoutOld = planBeforeRegen.filter(l => l.ai_batch_id!==batchIdToRegenerate);
      const newPlan = firstIdx>=0 ? [...withoutOld.slice(0,firstIdx),...newLayouts,...withoutOld.slice(firstIdx)] : [...withoutOld,...newLayouts];
      combinedLayoutPlanRef.current = newPlan; // Update ref immediately
      setCurrentCombinedLayoutPlan(newPlan);
      setLayoutProgress(90);
      const regeneratedBookRender = renderStoryBookWithLayout(newPlan, logLayout, FALLBACK_PREVIEW_SPREAD_WIDTH_PX, handleAlbumDataLoaded, handleRequestEditBatch, handleLayoutPlanUpdate, handlePhotoCropChangeInPlan, currentAlbumId);
      setRenderedBook(regeneratedBookRender);
      setSaveStatus({ visible: true, message: "Layout set regenerated!", success: true });
    } catch (e) {
      console.error(e);
      setSaveStatus({ visible: true, message: `Error regenerating set: ${e.message}`, success: false });
    } finally {
      setLoading(false); setShowLayoutProgress(false); setLayoutProgress(0);
    }
  };

  const handleLayoutPlanUpdate = useCallback((layoutIndex, placeholderIdxOnSpread, newOverrides) => {
    if (!combinedLayoutPlanRef.current || !combinedLayoutPlanRef.current[layoutIndex]) {
      logError("Layout plan placeholder update failed: Invalid layoutIndex or plan not ready.", { layoutIndex });
      return;
    }

    const updatedPlan = [...combinedLayoutPlanRef.current]; // Shallow copy the array of layouts
    // Ensure the layout at layoutIndex is an object before trying to spread it
    if (typeof updatedPlan[layoutIndex] !== 'object' || updatedPlan[layoutIndex] === null) {
        logError("Layout plan placeholder update failed: Layout at index is not an object.", { layoutIndex, layout: updatedPlan[layoutIndex] });
        return;
    }
    const layoutToUpdate = { ...updatedPlan[layoutIndex] }; // Shallow copy the specific layout object

    let currentModsArray = layoutToUpdate.placeholderModifications
      ? [...layoutToUpdate.placeholderModifications] // Copy if exists
      : [];

    // Ensure the array is long enough.
    while (currentModsArray.length <= placeholderIdxOnSpread) {
      currentModsArray.push(null);
    }

    // If newOverrides is null, it means reset this specific placeholder's overrides.
    // Otherwise, merge with existing (if any) or set as new.
    currentModsArray[placeholderIdxOnSpread] = newOverrides === null ? null : {
      ...(currentModsArray[placeholderIdxOnSpread] || {}), // Keep existing parts of override if not fully replaced
      ...newOverrides
    };
    
    // If an override results in an empty object (and wasn't an explicit null from newOverrides), set it to null.
    // This cleans up cases where an override might be "emptied" rather than explicitly reset.
    if (newOverrides !== null && currentModsArray[placeholderIdxOnSpread] && Object.keys(currentModsArray[placeholderIdxOnSpread]).length === 0) {
        currentModsArray[placeholderIdxOnSpread] = null;
    }

    layoutToUpdate.placeholderModifications = currentModsArray;
    updatedPlan[layoutIndex] = layoutToUpdate;

    combinedLayoutPlanRef.current = updatedPlan;
    setCurrentCombinedLayoutPlan(updatedPlan); // This will trigger the useEffect below to re-render the book

    logInfo('Layout plan placeholder updated via App.js handler', { layoutIndex, placeholderIdxOnSpread });
  }, [logError, logInfo]);

  // Handle photo crop changes in the layout plan
  const handlePhotoCropChangeInPlan = useCallback((layoutIndex, photoIndexInLayout, newCropInfo) => {
    if (!combinedLayoutPlanRef.current || !combinedLayoutPlanRef.current[layoutIndex]) {
      logError("Photo crop update failed: Invalid layoutIndex or plan not ready.", { layoutIndex });
      return;
    }
    if (!combinedLayoutPlanRef.current[layoutIndex].photos || !combinedLayoutPlanRef.current[layoutIndex].photos[photoIndexInLayout]) {
      logError("Photo crop update failed: Invalid photoIndexInLayout or photos array not ready.", { layoutIndex, photoIndexInLayout });
      return;
    }

    const updatedPlan = [...combinedLayoutPlanRef.current];
    const layoutToUpdate = { ...updatedPlan[layoutIndex] };
    const photosToUpdate = [...layoutToUpdate.photos];
    const photoToUpdate = { ...photosToUpdate[photoIndexInLayout] };

    photoToUpdate.cropInfo = newCropInfo;
    photosToUpdate[photoIndexInLayout] = photoToUpdate;
    layoutToUpdate.photos = photosToUpdate;
    updatedPlan[layoutIndex] = layoutToUpdate;

    combinedLayoutPlanRef.current = updatedPlan;
    setCurrentCombinedLayoutPlan(updatedPlan); // This triggers the useEffect to re-render the book

    logInfo('Photo crop info updated in plan', { layoutIndex, photoIndexInLayout });
  }, [logError, logInfo]);

  // This useEffect will re-render the book whenever currentCombinedLayoutPlan changes.
  useEffect(() => {
    if (currentCombinedLayoutPlan && currentCombinedLayoutPlan.length > 0) {
      let spreadPreviewWidthPx = FALLBACK_PREVIEW_SPREAD_WIDTH_PX;
      let spreadPreviewHeightPx = FALLBACK_PREVIEW_SPREAD_HEIGHT_PX; // Ensure this is also defined
      if (storyBookDisplayAreaRef.current && storyBookDisplayAreaRef.current.clientWidth > 0) {
        const containerClientWidth = storyBookDisplayAreaRef.current.clientWidth;
        spreadPreviewWidthPx = containerClientWidth * 0.90;
        spreadPreviewHeightPx = spreadPreviewWidthPx / 1.414; 
      }
       if (spreadPreviewWidthPx <= 0 || spreadPreviewHeightPx <= 0) {
        spreadPreviewWidthPx = FALLBACK_PREVIEW_SPREAD_WIDTH_PX;
        spreadPreviewHeightPx = FALLBACK_PREVIEW_SPREAD_HEIGHT_PX;
      }

      const bookRender = renderStoryBookWithLayout(
        currentCombinedLayoutPlan,
        logLayout,
        spreadPreviewWidthPx,
        handleAlbumDataLoaded,
        handleRequestEditBatch,
        handleLayoutPlanUpdate, // Pass the new handler
        handlePhotoCropChangeInPlan, // ADD THIS
        currentAlbumId
      );
      setRenderedBook(bookRender);
    } else if (currentCombinedLayoutPlan === null || (Array.isArray(currentCombinedLayoutPlan) && currentCombinedLayoutPlan.length === 0)) {
      setRenderedBook(null); // Clear rendered book if plan is empty or null
    }
  // Key dependencies: currentCombinedLayoutPlan triggers it. Others are stable callbacks or values affecting rendering.
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [currentCombinedLayoutPlan, logLayout, handleAlbumDataLoaded, handleRequestEditBatch, handleLayoutPlanUpdate, handlePhotoCropChangeInPlan]);

  // Add function to handle photo timestamp click
  const handlePhotoTimestampClick = (photo) => {
    if (!photo.date_taken) return;
    
    // Extract time from photo timestamp
    const photoDate = new Date(photo.date_taken);
    const hours = photoDate.getHours().toString().padStart(2, '0');
    const minutes = photoDate.getMinutes().toString().padStart(2, '0');
    const timeString = `[${hours}:${minutes}] `;
    
    // Append timestamp to the end of the current content
    const newContent = (currentEntry?.content || '') + timeString;
    
    // Update the entry
    setCurrentEntry(prevEntry => ({ ...prevEntry, content: newContent }));
    
    // The focus will be handled by the JournalEntry component when it receives the updated content
  };

  return (
    <Container>
      <ProgressContext.Provider value={progressContextValue}>
        {loading && <LoadingAnimation />}
        {!user ? (
          <Button onClick={handleLogin} variant="contained" color="primary">
            Login with Google
          </Button>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Box>
                  <Typography variant="h6">Welcome, {user.displayName || user.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Status: {token ? '✅ Connected to Google Services' : '❌ Not Connected'} | 
                    Auth: {user ? '✅ Authenticated' : '❌ Not Authenticated'}
                  </Typography>
                </Box>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  onClick={handleLogout}
                  size="small"
                >
                  Logout
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <AlbumSwitcher />
            </Grid>
            <Grid item xs={12} md={8}>
              {showTokenInput && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <TextField
                    label="Enter ChatGPT Token"
                    variant="outlined"
                    value={chatGptToken}
                    onChange={(e) => setChatGptToken(e.target.value)}
                    sx={{ mb: 2, width: "300px" }}
                  />
                  <Button variant="contained" color="primary" onClick={saveToken}>
                    Save Token
                  </Button>
                </Box>
              )}
              <JournalAssistant analysis={analysisResult} />
              <JournalEntry
                selectedDate={selectedDate}
                currentEntry={currentEntry}
                setCurrentEntry={setCurrentEntry}
                setLoading={setLoading}
                setAnalysisResult={setAnalysisResult}
                analysisResult={analysisResult}
                saveEntry={saveEntry}
                openGeoPhotoPopup={handleGeoPopupOpen} 
              />
              <CalendarView
                selectedDate={selectedDate}
                setSelectedDate={handleDateSelect}
              />
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenGooglePhotosPicker}
                  disabled={!user || !token}
                >
                  📷 Select Photos from Google Photos
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCreateStoryBook}
                >
                  Create Story Book
                </Button>
                <label htmlFor="load-album-file-main">
                  <input
                    id="load-album-file-main"
                    type="file"
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        try {
                          const loadedData = JSON.parse(e.target.result);
                          // Verify album version
                          if (!loadedData.albumVersion) {
                            setSaveStatus({ visible: true, message: "Invalid album file format", success: false });
                            return;
                          }
                          handleAlbumDataLoaded(loadedData);
                        } catch (error) {
                          console.error('Error parsing album file:', error);
                          setSaveStatus({ visible: true, message: `Error loading album: ${error.message}`, success: false });
                        }
                      };
                      reader.readAsText(file);
                      // Reset the input so the same file can be selected again
                      e.target.value = '';
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    component="span"
                  >
                    Load Album
                  </Button>
                </label>
                <Button
                  variant="contained"
                  color="info" 
                  onClick={handleCreateStoryText}
                >
                  Generate Story Text
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setShowLayoutEditor(true)}
                  sx={{ ml: 1 }}
                >
                  ✨ Edit Layouts
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" component="div">
                  Selected Photos ({currentEntry.photos.length})
                </Typography>
                {currentEntry.photos.length > 0 ? (
                  <div className="photos-container">
                    <div className="photos-grid">
                      {currentEntry.photos.map((photo) => (
                        <div key={`selected-${photo.id}`} className="photo-item">
                          <img src={photo.baseUrl} alt={photo.filename} />
                          {photo.latitude && photo.longitude && (
                            <img
                              src={MapIcon}
                              alt="map"
                              style={{
                                position: 'absolute',
                                top: '0px',
                                right: '0px',
                                width: '25px',
                                height: '25px',
                              }}
                            />
                          )}
                          {/* Notebook icon for adding timestamp */}
                          {photo.date_taken && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePhotoTimestampClick(photo);
                              }}
                              sx={{
                                position: 'absolute',
                                top: '2px',
                                right: photo.latitude && photo.longitude ? '27px' : '2px', // Adjust position if map icon is present
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                color: '#1976d2',
                                width: '20px',
                                height: '20px',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                },
                              }}
                            >
                              <MenuBookIcon fontSize="small" />
                            </IconButton>
                          )}
                          <label className="select-checkbox">
                            <input
                              type="checkbox"
                              checked={true}
                              onChange={(e) => handlePhotoSelectionChange(photo, e.target.checked)}
                            />
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Typography>No photos selected. Use the "Select Photos" button to add photos from Google Photos.</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      
      {/* Upload Progress Display */}
      {showUploadProgress && (
        <Box sx={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', width: '80%', maxWidth: '600px', zIndex: 1002 }}>
          <Paper elevation={4} sx={{ p: 2 }}>
            <Typography variant="caption">{uploadProgressLabel}</Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" align="right">{uploadProgress}%</Typography>
          </Paper>
        </Box>
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={saveStatus.visible}
        autoHideDuration={6000}
        onClose={() => setSaveStatus({ ...saveStatus, visible: false })}
      >
        <Alert
          onClose={() => setSaveStatus({ ...saveStatus, visible: false })}
          severity={saveStatus.success ? "success" : "error"}
          sx={{ width: '100%' }}
        >
          {saveStatus.message}
        </Alert>
      </Snackbar>
      {geoPopupPhotos && (
        <PhotoPopupWithGeo photos={geoPopupPhotos} closePhotoPopup={closeGeoPhotoPopup} fetchMorePlacesForGroup={fetchMorePlacesForGroup} fetchPlaceForGroup={fetchPlaceForGroup} />
      )}
      {showStoryBookPopup && (
        <StoryBookPopup
          selectedDates={storyBookDates}
          setSelectedDates={setStoryBookDates}
          onClose={handleCloseStoryBookPopup}
          onGenerate={handleGenerateStoryBook}
          title="Select Dates for Story Book"
          generateButtonText="Generate Story Book"
        />
      )}
      {showStoryTextPopup && (
        <StoryBookPopup
          selectedDates={storyTextDates}
          setSelectedDates={setStoryTextDates}
          onClose={handleCloseStoryTextPopup}
          onGenerate={handleGenerateStoryText}
          title="Select Dates for Story Text"
          generateButtonText="Generate Text & Copy"
        />
      )}
      <Box ref={storyBookDisplayAreaRef} sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, width: '100%' }}></Box>
      {renderedBook && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>My Photo Book</Typography>
          <div>{renderedBook}</div>
          <TextField 
            label="Comment for ChatGPT (Optional)"
            variant="outlined"
            fullWidth
            multiline
            rows={2} 
            value={userComment}
            onChange={(e) => setUserComment(e.target.value)}
            sx={{ mt: 2, mb: 1 }} 
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleGenerateStoryBook()} 
            sx={{ mt: 1 }} 
          >
            Generate Story Book
          </Button>
        </Box>
      )}
      <Dialog
        open={showGeneratedTextModal}
        onClose={handleCloseGeneratedTextModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generated Story Text</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            fullWidth
            value={generatedStoryText}
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
            maxRows={20}
            sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.9rem', mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGeneratedTextModal} color="secondary">
            Close
          </Button>
          <Button onClick={handleCopyToClipboard} color="primary" variant="contained">
            Copy to Clipboard
          </Button>
        </DialogActions>
      </Dialog>
      {showLayoutProgress && (
        <Box sx={{ width: '100%', marginBottom: 2, marginTop: 2 }}>
          <Typography variant="caption">{layoutProgressLabel}</Typography>
          <LinearProgress variant="determinate" value={layoutProgress} />
          <Typography variant="caption" align="right">{layoutProgress}%</Typography>
        </Box>
      )}
      {showPdfProgress && (
        <Box sx={{ width: '100%', marginBottom: 2, marginTop: 2 }}>
          <Typography variant="caption">{pdfProgressLabel}</Typography>
          <LinearProgress variant="determinate" value={pdfProgress} />
          <Typography variant="caption" align="right">{pdfProgress}%</Typography>
        </Box>
      )}
      <LayoutSetEditPopup
        open={editPopupOpen}
        batchId={currentEditBatchId}
        layoutsInBatch={layoutsForCurrentEditBatch}
        onClose={() => setEditPopupOpen(false)}
        onRegenerate={handleRegenerateLayoutSet}
      />
      
      {/* Layout Editor Modal */}
      {showLayoutEditor && (
        <LayoutEditor onClose={() => setShowLayoutEditor(false)} />
      )}
    </ProgressContext.Provider>
  <LogComponent logs={logs} />
  </Container>
);
};

const AppWithLogProvider = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  return (
    <LogProvider>
      <AlbumProvider user={user}>
        <App />
      </AlbumProvider>
    </LogProvider>
  );
};

export default AppWithLogProvider;
